import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Stack, FormControl, InputLabel, Select, MenuItem, Chip, Autocomplete, Divider } from '@mui/material';
import { styled } from 'styled-components';
import { useDropzone } from 'react-dropzone';
import { IconButton } from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from "@mui/icons-material/Close";
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import BASE_API_URL from '../apis/index';
import CompRgstrdFormSubmitted from './Modals/CompRgstrdFormSubmitted';
import PricingSection2 from './ProductRegFromComponents/PricingSection2';

// Products options
import { PRODUCT_OPTIONS } from '../constants/productsOptions';
const { formats, categories, features, grades } = PRODUCT_OPTIONS;

const UserProductRegistrationForm = ({ compId, compName, companyType, mode = "register", initialFormData }) => {
  const [loading, setLoading] = useState(false);
  const [tieredPricing, setTieredPricing] = useState(() => {
    if (mode === "edit" && Array.isArray(initialFormData?.tiered_pricing)) {
      return initialFormData.tiered_pricing;
    } else {
      return null;
    }
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [openError, setOpenError] = useState(false);

  const [formData, setFormData] = useState(initialFormData || {
    productName: '',
    description: '',
    category: '',
    tech_details: '',
    productType: '',
    concentration: '',
    source: '',
    features: [],
    grades: [],
    formats: '',
    pricing: '',
    maxPrice: '',
    minPrice: '',
    currency: 'INR',
    moq: '',
    moqUnit: 'kg',
    // coas: '',

    coas: null,
    image: null,
    documents: null,
  });

  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showRgstrdScsModal, setShowRgstrdScsModal] = useState(false);

  const handleChange = (field) => (e) => { setFormData((prev) => ({ ...prev, [field]: e.target.value })); };
  const handleCoaFileChange = (e) => { setFormData((prev) => ({ ...prev, coas: e.target.files[0] })); };

  const onDropImages = (acceptedFiles) => {
    const imageFiles = acceptedFiles.map(file =>
      Object.assign(file, { preview: URL.createObjectURL(file) })
    );
    setImages(prev => [...prev, ...imageFiles]);
  };

  const onDropDocuments = (acceptedFiles) => {
    const docFiles = acceptedFiles.map(file => ({
      id: uuidv4(),
      file,
      name: ''
    }));
    setDocuments(prev => [...prev, ...docFiles]);
  };

  const { getRootProps: getImageRootProps, getInputProps: getImageInputProps } = useDropzone({
    accept: { 'image/*': [] },
    onDrop: onDropImages
  });

  const { getRootProps: getDocRootProps, getInputProps: getDocInputProps } = useDropzone({
    accept: { 'application/pdf': [] },
    onDrop: onDropDocuments
  });

  const validateProductForm = () => {
    if ((images.length === 0) && (!formData.existingImages || formData.existingImages.length === 0)) {
      return "At least one product image is required";
    }

    if (!formData.productName?.trim()) return "Product Name is required";

    if (!formData.category) return "Category is required"; // ✅ fixed

    if (!formData.moq) return "MOQ is required";

    if (!formData.moqUnit) return "MOQ Unit is required"; // ✅ THIS is your actual unit

    if (formData.grades.length === 0) return "At least one grade is required";

    if (!formData.pricing) return "Pricing is required";

    if (!formData.currency?.trim()) return "Currency is required";

    return null;
  };

  const handleSubmit = async () => {
    const errorMsg = validateProductForm();
    if (errorMsg) {
      alert(errorMsg);
      return;
    }

    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');
      const submissionData = new FormData();

      // Common fields
      submissionData.append('company_id', compId);
      if (mode === "register") {
        submissionData.append('company_type', companyType);
      } else {
        submissionData.append('company_type', formData.company_type);
      }
      submissionData.append('product_name', formData.productName);
      submissionData.append('product_description', formData.description);
      submissionData.append('category', formData.category);
      submissionData.append('tech_details', formData.tech_details);
      submissionData.append('product_type', formData.productType);
      submissionData.append('concentration', formData.concentration);
      submissionData.append('source', formData.source);
      submissionData.append('formats', formData.formats);
      submissionData.append('pricing', formData.pricing);
      submissionData.append('min_price', formData.minPrice);
      submissionData.append('max_price', formData.maxPrice);
      submissionData.append('currency', formData.currency);
      submissionData.append('moq', `${formData.moq} ${formData.moqUnit}`);
      submissionData.append('features', JSON.stringify(formData.features));
      submissionData.append('grades', JSON.stringify(formData.grades));
      if (formData.coas) submissionData.append('coas', formData.coas);
      submissionData.append("tiered_pricing", JSON.stringify(tieredPricing));

      // ----------------- API CALL -----------------
      if (mode === "edit") {
        // EDIT mode
        submissionData.append('product_id', formData.product_id);

        // 🔹 Images (new uploads)
        if (images.length > 0) {
          images.forEach((img) => submissionData.append("productImages[]", img));
        } else {
          submissionData.append("productImages[]", ""); // empty to ensure backend always sees array
        }

        // 🔹 Existing images
        if (formData.existingImages?.length > 0) {
          formData.existingImages.forEach((img) =>
            submissionData.append("existingImages[]", img)
          );
        } else {
          submissionData.append("existingImages[]", ""); // force array
        }

        // 🔹 Documents (new)
        if (documents.length > 0) {
          documents.forEach((doc) => {
            submissionData.append("supportingDocuments[]", doc.file);
            submissionData.append("supportingDocumentNames[]", doc.name);
          });
        } else {
          submissionData.append("supportingDocuments[]", "");
          submissionData.append("supportingDocumentNames[]", "");
        }

        // 🔹 Existing documents
        if (formData.existingDocuments?.length > 0) {
          formData.existingDocuments.forEach((doc) => {
            submissionData.append("existingDocuments[]", doc.file);
            submissionData.append("existingDocumentNames[]", doc.name || "");
          });
        } else {
          submissionData.append("existingDocuments[]", "");
          submissionData.append("existingDocumentNames[]", "");
        }

        const res = await axios.put(
          `${BASE_API_URL}/api/seller/product/update/${formData.product_id}`,
          submissionData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${authToken}`
            },
          }
        );

        if (res.data.status === "ok") setShowRgstrdScsModal(true);
      } else {
        // REGISTER mode
        // Images
        images.forEach((img) => {
          if (img instanceof File) {
            submissionData.append('productImages', img);
          }
        });

        // Docs
        const documentNamesArray = documents.map((doc) => doc.name);
        documents.forEach((doc) => {
          if (doc.file) submissionData.append('supportingDocuments', doc.file);
        });
        submissionData.append('documentNames', JSON.stringify(documentNamesArray));

        const res = await axios.post(`${BASE_API_URL}/api/seller/product/register/${compId}`,
          submissionData,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (res.data.status === 'ok') setShowRgstrdScsModal(true);
      }
    } catch (err) {
      console.error(err);

      if (err?.response) {
        const status = err.response.status;
        const message = err.response.data?.message;

        if (status === 409) {
          setErrorMsg(message || "Product already exists");
          setOpenError(true);
          return;
        }

        if (status === 400) {
          alert(message || "Invalid product data.");
          return;
        }

        if (status === 401) {
          alert("Session expired. Please login again.");
          return;
        }
      }

      // fallback
      alert("Something went wrong while submitting product.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      {/* 🔽 Dropzone - Image Upload */}
      <Box className="fileDropBox" {...getImageRootProps()}>
        <input {...getImageInputProps()} />
        <Typography>Upload Product Images <span className="text-danger">*</span></Typography>
      </Box>

      <Box className="mt-2">
        {
          mode === 'edit' ? (
            <div className='d-flex gap-3 mt-4 flex-wrap'>
              {/* Existing images */}
              {formData.existingImages?.map((img, i) => (
                <Box key={`ex-${i}`} className="position-relative inline-block">
                  <img
                    src={`${BASE_API_URL}/uploads/user_prod_docs/${img}`}
                    alt={`img-${i}`}
                    style={{ height: 80, borderRadius: 6, objectFit: "cover" }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    sx={{ position: "absolute", top: -8, right: -8 }}
                    className='removeImageIcon'
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        existingImages: prev.existingImages.filter((_, idx) => idx !== i),
                      }))
                    }
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              {/* Newly added */}
              {images.map((img, i) => (
                <Box key={`new-${i}`} position="relative">
                  <img
                    src={img.preview}
                    alt={`img-${i}`}
                    style={{ height: 80, borderRadius: 6, objectFit: "cover" }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    className='removeImageIcon'
                    sx={{ position: "absolute", top: -8, right: -8 }}
                    onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </div>
          ) : (
            <Stack direction="row" spacing={2} mt={1} flexWrap="wrap">
              {images.map((img, i) => (
                <div key={i} className="border position-relative inline-block">
                  <img src={img.preview} alt={`img-${i}`} style={{ height: 80, borderRadius: 6, objectFit: 'cover' }} />

                  {/* Remove Button */}
                  <IconButton
                    size="small"
                    color="error"
                    sx={{ position: 'absolute', top: -8, right: -8, background: 'white', boxShadow: 1, '&:hover': { background: '#f8d7da' } }}
                    onClick={() => setImages((prev) => prev.filter((_, index) => index !== i))}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </Stack>
          )
        }
      </Box>

      {/* 🔽 Dropzone - Documents Upload */}
      <Box className="fileDropBox mt-3" {...getDocRootProps()}>
        <input {...getDocInputProps()} />
        <Typography>Upload Necessary Documents</Typography>
      </Box>

      <Box mt={2}>
        {
          formData.existingDocuments?.map((doc, i) => (
            <Stack
              key={`ex-doc-${i}`}
              direction="row"
              spacing={2}
              alignItems="center"
              my={1}
              style={{ minWidth: "250px" }}
            >
              <div className="bg-light rounded p-2 d-flex align-items-center">
                <a
                  href={`${BASE_API_URL}/uploads/user_prod_docs/${doc.file}`}
                  target="_blank"
                  rel="noreferrer"
                  className="me-2 text-decoration-none txt_color_secondary"
                >
                  {doc.name} {/* ✅ show user-given name */}
                </a>
                <IconButton
                  color="error"
                  className="removeImageIcon"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      existingDocuments: prev.existingDocuments.filter(
                        (_, idx) => idx !== i
                      ),
                    }))
                  }
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </Stack>
          ))}
        {
          documents.map((doc) => (
            <Stack
              key={doc.id}
              direction="row"
              spacing={2}
              alignItems="center"
              my={1}
            >
              <Typography>{doc.file.name}</Typography>
              <TextField
                size="small"
                label="Document Name"
                value={doc.name}
                onChange={(e) =>
                  setDocuments((prev) =>
                    prev.map((d) =>
                      d.id === doc.id ? { ...d, name: e.target.value } : d
                    )
                  )
                }
              />
              <IconButton
                className="border"
                color="error"
                onClick={() =>
                  setDocuments((prev) => prev.filter((d) => d.id !== doc.id))
                }
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          ))
        }
      </Box>

      {/* 🔽 Form Fields */}
      <Box>
        <TextField label={<> Product Name <span className='text-danger'>*</span> </>} className="mt-3 mb-2 w-100" value={formData.productName} onChange={handleChange('productName')} />
        <TextField label="Description" multiline rows={6} className="w-100 mb-2 mt-1" value={formData.description} onChange={handleChange('description')} />
        <TextField className="w-100 my-3" label="Technical Details (If any)" value={formData.tech_details} onChange={handleChange('tech_details')} />

        <Stack direction={'row'} spacing={2} mb={2}>
          <Autocomplete
            className="w-100"
            options={categories} // plain strings
            renderInput={(params) => (<TextField {...params} label={<> Category <span style={{ color: "red" }}>*</span> </>} />)}
            value={formData.category || null}
            onChange={(event, newValue) => setFormData({ ...formData, category: newValue })}
          />

          <FormControl className='w-100'>
            <InputLabel>Product Type</InputLabel>
            <Select value={formData.productType} label="Product Type" onChange={(e) => {
              setFormData((prev) => ({ ...prev, productType: e.target.value }));
            }}>
              <MenuItem value="Generic"> Generic </MenuItem>
              <MenuItem value="Branded"> Branded </MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* 🔽 Product Types */}
        <Stack direction={'row'} spacing={2} mb={2}>
          <TextField className='w-100' label="Concentration (%)" value={formData.concentration} onChange={handleChange('concentration')} />
          <TextField className='w-100' label="Source" value={formData.source} onChange={handleChange('source')} />
        </Stack>

        {/* 🔽 Formats, Price & Currency */}
        <Stack direction={'row'} spacing={2} mb={2}>
          <Autocomplete
            className="w-100"
            options={formats} // plain strings
            renderInput={(params) => (<TextField {...params} label="Formats Available" />)}
            value={formData.formats || null}
            onChange={(event, newValue) => setFormData({ ...formData, formats: newValue })}
          />
          <div className="w-100 d-flex align-items-center">
            <Typography fontWeight={400} className='pe-4 w-30'>CoAs Document :</Typography>
            {formData.existingCoas && typeof formData.existingCoas === "string" ? (
              <div className="mb-2 border d-flex align-items-center gap-3 p-2 rounded" style={{ width: "220px" }}>
                <a
                  href={`${BASE_API_URL}/uploads/user_prod_docs/${formData.existingCoas}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-decoration-none txt_color_secondary"
                >
                  View Existing File
                </a>
                <Divider flexItem orientation="vertical" />
                <button
                  className="btn p-0"
                  onClick={() => setFormData(prev => ({ ...prev, existingCoas: null }))}
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            ) : (
              <TextField className='w-70' type="file" inputProps={{ accept: 'application/pdf' }} onChange={handleCoaFileChange} />
            )}
          </div>
        </Stack>

        <PricingSection2
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          tieredPricing={tieredPricing}
          setTieredPricing={setTieredPricing}
        />

        {/* 🔽 Features */}
        <Stack direction={'row'} flexWrap={'wrap'} mb={3} className='align-items-center mt-4'>
          <p className='mb-2 fw-medium'>Features :</p>
          <Stack direction="row" gap={1} flexWrap="wrap" className='dosageTypesField'>
            {features.map((option) => (
              <Chip
                key={option}
                label={option}
                variant={formData.features.includes(option) ? 'filled' : 'outlined'}
                color={formData.features.includes(option) ? 'warning' : 'default'}
                icon={formData.features.includes(option) ? <CheckIcon /> : null}
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    features: prev.features.includes(option)
                      ? prev.features.filter((item) => item !== option)
                      : [...prev.features, option]
                  }));
                }}
              />
            ))}
          </Stack>
        </Stack>

        {/* 🔽 Grades */}
        <Stack direction={'row'} spacing={2} className='align-items-center'>
          <Typography fontWeight={500}>Grades<span className='text-danger'>*</span> :</Typography>
          <Stack direction="row" gap={1} flexWrap="wrap" className='dosageTypesField'>
            {grades.map((option) => (
              <Chip
                key={option}
                label={option}
                variant={formData.grades.includes(option) ? 'filled' : 'outlined'}
                color={formData.grades.includes(option) ? 'warning' : 'default'}
                icon={formData.grades.includes(option) ? <CheckIcon /> : null}
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    grades: prev.grades.includes(option)
                      ? prev.grades.filter((item) => item !== option)
                      : [...prev.grades, option]
                  }));
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Box>

      <Button
        variant="contained"
        color="warning"
        onClick={handleSubmit}
        sx={{ mt: 3 }}
        className="w-100 py-3 shadow-none"
        disabled={loading}
      >
        {loading
          ? mode === "edit"
            ? "Updating, Please wait!"
            : "Registering, Please wait!"
          : mode === "edit"
            ? "Update Product"
            : "Submit Product"}
      </Button>

      <CompRgstrdFormSubmitted
        show={showRgstrdScsModal}
        onClose={() => setShowRgstrdScsModal(false)}
        redirectLink={`/user-company-details?compId=${compId}`}
      />

      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={openError} autoHideDuration={4000} onClose={() => setOpenError(false)}>
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>
    </Wrapper>
  );
};

export default UserProductRegistrationForm;

const Wrapper = styled(Box)`
  margin: 50px auto;
  .fileDropBox {
    padding: 20px;
    border: 2px dashed #ccc;
    border-radius: 8px;
    text-align: center;
    background: #f8f8f8;
    cursor: pointer;
  }
  .MuiChip-root {
    cursor: pointer;
    padding: 10px;
    font-size: 14px;
    border-radius: 8px;
  }
  .MuiChip-filled { 
        span { color: white !important; } 
        svg { fill: white; }
    }
  .removeImageIcon{
    background: white;
    border: 1px solid red;
    svg{ fill: red; }
  }
`;
